import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Alert
} from '@mui/material';
import { Lock as LockIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import adminPaymentAuthService from '../../services/adminPaymentAuthService';
import { getCurrencySymbol } from '../../utils/currency';
import { getStripePromise, reloadStripePromise } from '../../utils/stripeLoader';

/**
 * Modal 3DS isolé pour les décisions administratives
 * Complètement indépendant du flux de résolution amiable
 */
function AdminPaymentAuth3DSModal({
  open,
  onClose,
  authRequest,
  onSuccess,
  currency = 'EUR'
}) {
  const { t } = useTranslation('disputes');
  const { enqueueSnackbar } = useSnackbar();
  const [processing, setProcessing] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && authRequest) {
      handleStripe3DS();
    }
  }, [open, authRequest]);

  const handleStripe3DS = async () => {
    if (!authRequest || !authRequest.client_secret) {
      setError(t('admin3DS.missingInfo'));
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      let stripe = await getStripePromise();
      if (!stripe) {
        stripe = await reloadStripePromise();
      }

      if (!stripe) {
        throw new Error(t('admin3DS.stripeNotInitialized'));
      }

      console.log('🔐 [AdminPaymentAuth3DS] Handling payment confirmation', {
        authRequestId: authRequest.id,
        clientSecret: authRequest.client_secret?.substring(0, 20) + '...',
        hasPaymentMethod: !!authRequest.payment_method_id
      });

      // Confirmer le PaymentIntent avec Stripe
      // Si le PaymentIntent a status requires_payment_method, il faut fournir le payment_method
      const confirmOptions = {
        payment_method: authRequest.payment_method_id
      };

      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        authRequest.client_secret,
        confirmOptions
      );

      if (stripeError) {
        console.error('❌ [AdminPaymentAuth3DS] Stripe error:', stripeError);
        // Traduire via le code Stripe (invariant, langue-agnostique) plutôt que stripeError.message
        // qui est généré par Stripe dans la langue du navigateur et ne passe pas par i18n
        const errorCode = stripeError.code || stripeError.decline_code;
        const translatedMessage = errorCode
          ? t(`admin3DS.stripeErrors.${errorCode}`, { defaultValue: '' })
          : '';
        throw new Error(translatedMessage || t('admin3DS.authError'));
      }

      if (paymentIntent.status === 'succeeded') {
        console.log('✅ [AdminPaymentAuth3DS] Payment succeeded', {
          paymentIntentId: paymentIntent.id
        });

        // Confirmer côté backend
        await adminPaymentAuthService.confirmAuthentication(
          authRequest.id,
          paymentIntent.id
        );

        setAuthenticated(true);
        enqueueSnackbar(
          t('admin3DS.successMessage'),
          { variant: 'success' }
        );

        // Attendre un peu avant de fermer
        setTimeout(() => {
          onSuccess?.();
          onClose();
        }, 2000);

      } else {
        throw new Error(t('admin3DS.unexpectedStatus', { status: paymentIntent.status }));
      }

    } catch (err) {
      console.error('❌ [AdminPaymentAuth3DS] Error:', err);
      setError(err.message || t('admin3DS.authError'));
      enqueueSnackbar(
        t('admin3DS.errorPrefix', { message: err.message }),
        { variant: 'error' }
      );
    } finally {
      setProcessing(false);
    }
  };

  const handleCancel = () => {
    // Ferme simplement le modal sans annuler la demande
    // Le renter peut annuler via le bouton "Refuser" dans la banner
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={authenticated ? onClose : handleCancel}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={processing}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {authenticated ? (
          <>
            <CheckCircleIcon color="success" />
            {t('admin3DS.titleSuccess')}
          </>
        ) : (
          <>
            <LockIcon color="primary" />
            {t('admin3DS.title')}
          </>
        )}
      </DialogTitle>

      <DialogContent>
        {processing && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {t('admin3DS.processingAuth')}
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {authenticated ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {t('admin3DS.paymentAuthSuccess')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('admin3DS.processingPayment')}
            </Typography>
          </Box>
        ) : (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('admin3DS.bankAuthRequired')}
            </Alert>
            
            <Typography variant="body2" gutterBottom>
              <strong>{t('admin3DS.amount')}</strong> {authRequest?.amount}{getCurrencySymbol(currency)}
            </Typography>
            
            {authRequest?.decision && (
              <Typography variant="body2" color="text.secondary">
                <strong>{t('admin3DS.type')}</strong> {t('admin3DS.adminDecision')}
              </Typography>
            )}
            
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              {t('admin3DS.authWindowInfo')}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {!authenticated && (
          <>
            <Button
              onClick={handleCancel}
              disabled={processing}
              color="inherit"
            >
              {t('admin3DS.cancel')}
            </Button>
            {error && (
              <Button
                onClick={handleStripe3DS}
                disabled={processing}
                variant="contained"
                color="primary"
              >
                {t('admin3DS.retry')}
              </Button>
            )}
          </>
        )}
        {authenticated && (
          <Button onClick={onClose} variant="contained" color="success">
            {t('admin3DS.close')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default AdminPaymentAuth3DSModal;
